// Every Firestore access for up-country (large truck) booking. Pages never touch
// `db` -- the same contract bookingService.js and dispatchService.js keep.
const { db } = require('@/firebase')
const R = require('./bookingRules')
const U = require('./upcountry')
const X = require('./proposalRules')
const RJ = require('./returnJob')
const P = require('./upcountryPlan')
const DR = require('./deliveryRow')

const SLOT_COL = () => db.collection('bookingSlot')
const TRIP_COL = () => db.collection('upcountryTrip')
const HOLD_REF = plate => db.collection('truckHold').doc(plate)
const CONFIG_REF = () => db.collection('upcountryConfig').doc('default')
const PLAN_CFG_REF = () => db.collection('upcountryPlanConfig').doc('default')
const LIST_COL = () => db.collection('deliveryList')
const TIMELINE_COL = () => db.collection('deliveryTimeline')
const SHOP_COL = () => db.collection('shopClosedDay')
const INSTALL_MODES = ['install', 'drop', 'unknown']

const now = () => new Date().toISOString()
const log = (b, entry) => (b.history || []).concat([Object.assign({ at: now() }, entry)])

async function getUpConfig () {
  const snap = await CONFIG_REF().get()
  return U.normalizeConfig(snap.exists ? snap.data() : null)
}

// A new order's document, pooled. Booking onto a trip starts from the same one.
const newOrderDoc = (order, role) => Object.assign({}, order, {
  soNo: String(order.soNo || '').trim(),
  fleet: 'large',
  upGroup: U.groupsOf(order.upProvince),
  upTripId: null,
  date: null,
  slot: 'full',
  installMode: INSTALL_MODES.indexOf(order.installMode) !== -1 ? order.installMode : 'unknown',
  orderValue: Number(order.orderValue),
  outOfArea: false,
  tripId: null,
  status: 'active',
  orderDate: order.orderDate || R.todayStr(),
  telDigits: R.normalizeTel(order.telephone),
  history: [{ action: 'upBook', week: order.earliestWeek, by: order.user || '', role: role || '', at: now() }],
  createdAt: now()
})

// Into the pool, or -- with tripId -- straight onto a trip already fixed, when
// U.checkJoinTrip allows it. No bookingDay is read or written: an up-country
// order has no seat on a day. Booking onto a trip leaves truckHold alone (the
// trip's dates do not move), and a refusal writes nothing at all: it never
// falls back to the pool, since the booker may have told the customer a date.
async function createUpOrder (order, { role, tripId } = {}) {
  if (!tripId) {
    const check = U.checkUpOrder(order, R.todayStr())
    if (!check.ok) throw new R.BookingError(check.reason)
    const ref = SLOT_COL().doc()
    await ref.set(newOrderDoc(order, role))
    return ref.id
  }
  const cfg = await getUpConfig()
  const ref = SLOT_COL().doc()
  await db.runTransaction(async t => {
    const { ref: tRef, trip } = await readTrip(t, tripId)
    const o = Object.assign({}, order, { earliestWeek: U.mondayOf(trip.startDate) })
    const today = R.todayStr()
    const check = U.checkUpOrder(o, today)
    if (!check.ok) throw new R.BookingError(check.reason)
    const join = U.checkJoinTrip(trip, o, cfg, today)
    if (!join.ok) throw new R.BookingError(join.reason)
    const doc = newOrderDoc(o, role)
    t.set(ref, Object.assign(doc, {
      upTripId: tripId,
      upRegNum: trip.regNum,
      date: trip.startDate,
      upGroup: U.narrowGroup(doc.upGroup, trip.groups),
      history: doc.history.concat([{ action: 'upFix', tripId, date: trip.startDate, regNum: trip.regNum, by: order.user || '', at: now() }])
    }))
    t.update(tRef, Object.assign(U.tripAfterAdd(trip, Object.assign({ id: ref.id }, o), cfg), {
      history: (trip.history || []).concat([{ action: 'addOrder', orderId: ref.id, booked: true, by: order.user || '', at: now() }])
    }))
  })
  return ref.id
}

async function readOrder (t, id) {
  const ref = SLOT_COL().doc(id)
  const snap = await t.get(ref)
  if (!snap.exists) throw new R.BookingError('NOT_FOUND')
  const b = snap.data()
  if (!U.isUpcountry(b)) throw new R.BookingError('NOT_FOUND')
  return { ref, b: Object.assign({ id }, b) }
}

async function setInstallMode (id, { mode, user, role }) {
  if (!R.isStaffRole(role)) throw new R.BookingError('UP_NOT_ALLOWED')
  if (INSTALL_MODES.indexOf(mode) === -1) throw new R.BookingError('UP_BAD_MODE')
  await db.runTransaction(async t => {
    const { ref, b } = await readOrder(t, id)
    t.update(ref, { installMode: mode, history: log(b, { action: 'upInstallMode', from: b.installMode, to: mode, by: user || '' }) })
  })
}

// The writes of a cancellation, shared with proposalService.confirmProposal.
// Reads first (the trip, when the order is on one), then writes it; the caller
// writes the returned patch onto the order. `b.id` is required: it is what
// tripAfterRemove takes the order off the trip's orderIds by.
async function upCancelWrites (t, b, { user, reason }) {
  if (b.upTripId) {
    const tripRef = TRIP_COL().doc(b.upTripId)
    const tripSnap = await t.get(tripRef)
    const cfg = await getUpConfig()
    if (tripSnap.exists) {
      const trip = tripSnap.data()
      // A sent trip's route may be keyed in managecar: the order leaves through
      // the change-a-sent-trip path, which also tells SAP and the driver's sheet.
      if (trip.status === 'sent') throw new R.BookingError('UP_ORDER_SENT')
      t.update(tripRef, Object.assign(U.tripAfterRemove(trip, b.id, b, cfg), {
        history: (trip.history || []).concat([{ action: 'orderCancelled', orderId: b.id, by: user || '', at: now() }])
      }))
    } else {
      t.set(tripRef, {}, { merge: true }) // read must be written (Web SDK v1)
    }
  }
  return cancelPatch(b, { user, reason })
}

// The order's own patch for a cancellation, shared by every path that cancels one.
function cancelPatch (b, { user, reason }) {
  return {
    status: 'cancelled',
    cancelledAt: now(),
    cancelledBy: user || '',
    history: log(b, { action: 'cancel', from: { date: b.date, week: b.earliestWeek }, reason: reason || '', by: user || '' })
  }
}

async function cancelUpOrder (id, { user, reason, role, identities }) {
  await db.runTransaction(async t => {
    const { ref, b } = await readOrder(t, id)
    if (!U.canManageOrder(b, role, identities)) throw new R.BookingError('UP_NOT_ALLOWED')
    if (!R.isActive(b.status)) { t.update(ref, { status: 'cancelled' }); return }
    if (X.isPending(b)) throw new R.BookingError('PROPOSAL_PENDING')
    t.update(ref, await upCancelWrites(t, b, { user, reason }))
  })
}

// Every live up-country order: two equalities, served by automatic indexes.
function subscribeOrders (cb, onError) {
  return SLOT_COL().where('fleet', '==', 'large').where('status', '==', 'active')
    .onSnapshot(snap => {
      const rows = []
      snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
      cb(rows)
    }, onError)
}

async function readHold (t, plate) {
  const ref = HOLD_REF(plate)
  const snap = await t.get(ref)
  return { ref, holds: snap.exists ? (snap.data().holds || []) : [] }
}

async function readTrip (t, tripId) {
  const ref = TRIP_COL().doc(tripId)
  const snap = await t.get(ref)
  if (!snap.exists) throw new R.BookingError('UP_TRIP_CLOSED')
  return { ref, trip: snap.data() }
}

// Back to the pool: no date, no trip, both groups again for an overlap province.
const pooledPatch = (b, week, entry) => ({
  upTripId: null,
  upRegNum: null,
  date: null,
  earliestWeek: week || b.earliestWeek,
  upGroup: U.groupsOf(b.upProvince),
  history: log(b, entry)
})

async function fixTrip ({ groups, startDate, returnDate, regNum, orderIds, user }) {
  const cfg = await getUpConfig()
  const tripRef = TRIP_COL().doc()
  let warnings = []
  await db.runTransaction(async t => {
    const hold = await readHold(t, regNum)
    const read = []
    for (const id of orderIds) read.push(await readOrder(t, id))
    const orders = read.map(r => r.b)
    const check = U.checkFix({ orders, groups, startDate, returnDate, regNum, holds: hold.holds, cfg, today: R.todayStr() })
    if (!check.ok) throw new R.BookingError(check.reason)
    warnings = check.warnings
    const tot = U.totals(orders, cfg)
    t.set(tripRef, {
      startDate, groups, regNum, returnDate,
      orderIds: orders.map(b => b.id),
      items: tot.items, volume: tot.volume, value: tot.value, belowMin: tot.value < cfg.minValue,
      status: 'fixed', actualReturn: null,
      createdBy: user || '', createdAt: now(),
      history: [{ action: 'fix', regNum, startDate, returnDate, orders: orders.length, warnings, by: user || '', at: now() }]
    })
    t.set(hold.ref, { holds: U.holdsAfterFix(hold.holds, tripRef.id, U.loadingDay(startDate), returnDate) })
    read.forEach(({ ref, b }) => t.update(ref, {
      upTripId: tripRef.id,
      upRegNum: regNum,
      date: startDate,
      upGroup: U.narrowGroup(b.upGroup, groups),
      history: log(b, { action: 'upFix', tripId: tripRef.id, date: startDate, regNum, by: user || '' })
    }))
  })
  return { tripId: tripRef.id, warnings }
}

async function addToTrip (tripId, orderId, { user, today }) {
  const cfg = await getUpConfig()
  await db.runTransaction(async t => {
    const { ref: tRef, trip } = await readTrip(t, tripId)
    const { ref, b } = await readOrder(t, orderId)
    const editable = U.checkAddable(trip, today || R.todayStr())
    if (!editable.ok) throw new R.BookingError(editable.reason)
    if (!U.isPooled(b)) throw new R.BookingError('UP_NOT_POOLED')
    if (X.isPending(b)) throw new R.BookingError('PROPOSAL_PENDING')
    if (!(b.upGroup || []).some(k => trip.groups.indexOf(k) !== -1)) throw new R.BookingError('UP_WRONG_GROUP')
    if (String(b.earliestWeek) > U.mondayOf(trip.startDate)) throw new R.BookingError('UP_TOO_EARLY_WEEK')
    // On a sent trip the new order is not on SAP or the sheet yet: pending is what
    // shows ส่งไม่ครบ / ลองอีกครั้ง on the card.
    t.update(tRef, Object.assign(U.tripAfterAdd(trip, b, cfg), trip.status === 'sent' ? { sendState: 'pending' } : {}, {
      history: (trip.history || []).concat([{ action: 'addOrder', orderId, by: user || '', at: now() }])
    }))
    t.update(ref, {
      upTripId: tripId, upRegNum: trip.regNum, date: trip.startDate, upGroup: U.narrowGroup(b.upGroup, trip.groups),
      history: log(b, { action: 'upFix', tripId, date: trip.startDate, regNum: trip.regNum, by: user || '' })
    })
  })
}

// The customer moved by a week: off the trip, back to the pool. Staff only;
// a shop proposes (Phase 1: cancel only -- see the spec).
// Off its trip and back to the pool -- the one transaction behind both
// postponeOrder (the customer moved: a new week, checked like a new order's) and
// removeFromTrip (the order should not be on this trip: the week is kept, so an
// overlap order can go on the other group's truck). Staff only; a shop proposes.
async function takeOffTrip (orderId, { week, user, role }) {
  if (!R.isStaffRole(role)) throw new R.BookingError('UP_NOT_ALLOWED')
  const cfg = await getUpConfig()
  await db.runTransaction(async t => {
    const { ref, b } = await readOrder(t, orderId)
    if (!R.isActive(b.status)) throw new R.BookingError('ALREADY_CANCELLED')
    if (!b.upTripId) throw new R.BookingError('UP_NOT_ON_TRIP')
    if (X.isPending(b)) throw new R.BookingError('PROPOSAL_PENDING')
    const { ref: tRef, trip } = await readTrip(t, b.upTripId)
    const editable = U.checkTripEditable(trip)
    if (!editable.ok) throw new R.BookingError(editable.reason)
    if (week) {
      const check = U.checkUpOrder(Object.assign({}, b, { earliestWeek: week }), R.todayStr())
      if (!check.ok) throw new R.BookingError(check.reason)
    }
    const tripAction = week ? 'postpone' : 'removeOrder'
    const orderEntry = week
      ? { action: 'upPostpone', from: b.date, week, by: user || '' }
      : { action: 'upRemove', from: b.date, tripId: b.upTripId, by: user || '' }
    t.update(tRef, Object.assign(U.tripAfterRemove(trip, orderId, b, cfg), {
      history: (trip.history || []).concat([{ action: tripAction, orderId, week: week || null, by: user || '', at: now() }])
    }))
    t.update(ref, pooledPatch(b, week || null, orderEntry))
  })
}

// อัปเดตจาก SAP: write what U.sapRefresh worked out from GetSo / GetProdList,
// read by the view (SAP calls never run inside a service). Re-checked here: an
// order that joined a trip or was cancelled while the button ran is refused,
// because a trip's totals were fixed with the figures it had.
async function refreshFromSap (orderId, { patch, from, user, role }) {
  if (!R.isStaffRole(role)) throw new R.BookingError('UP_NOT_ALLOWED')
  await db.runTransaction(async t => {
    const { ref, b } = await readOrder(t, orderId)
    if (!U.isPooled(b)) throw new R.BookingError('UP_NOT_POOLED')
    const to = { itemCount: patch.itemCount, totalVolume: patch.totalVolume, orderValue: patch.orderValue }
    if (patch.rtNo) to.rtNo = patch.rtNo
    t.update(ref, Object.assign({}, patch, {
      history: log(b, { action: 'upRefresh', from: from || {}, to, by: user || '' })
    }))
  })
}

// นำออกจากคิว: a waiting order whose SO SAP already holds on a ใบจัดรถ (keyed by
// hand in managecar, U.sapRefresh's `dispatched`) leaves the pool as cancelled,
// the reason naming the sheet. Only the order is written: the sheet's rows,
// their timelines and SAP stay as they are -- the delivery is real. Refused for
// an order that joined a trip or holds a pending request meanwhile.
async function retireDispatched (orderId, { transNo, user, role }) {
  if (!R.isStaffRole(role)) throw new R.BookingError('UP_NOT_ALLOWED')
  if (!String(transNo || '').trim()) throw new R.BookingError('UP_NOT_POOLED')
  await db.runTransaction(async t => {
    const { ref, b } = await readOrder(t, orderId)
    if (!U.isPooled(b)) throw new R.BookingError('UP_NOT_POOLED')
    if (X.isPending(b)) throw new R.BookingError('PROPOSAL_PENDING')
    t.update(ref, cancelPatch(b, { user, reason: `ส่งแล้วด้วยใบจัดรถ ${String(transNo).trim()} (คีย์เองในจัดรถ)` }))
  })
}

// The customer moved by a week.
function postponeOrder (orderId, { week, user, role }) {
  if (!week) throw new R.BookingError('UP_BAD_WEEK')
  return takeOffTrip(orderId, { week, user, role })
}

// นำออกจากรอบ: off this trip, the customer's week unchanged.
function removeFromTrip (orderId, { user, role }) {
  return takeOffTrip(orderId, { week: null, user, role })
}

async function setReturnDate (tripId, { returnDate, user }) {
  await db.runTransaction(async t => {
    const { ref: tRef, trip } = await readTrip(t, tripId)
    const hold = await readHold(t, trip.regNum)
    if (trip.status === 'cancelled' || trip.status === 'done') throw new R.BookingError('UP_TRIP_CLOSED')
    const check = U.checkReturnDate(Object.assign({ id: tripId }, trip), returnDate, hold.holds)
    if (!check.ok) throw new R.BookingError(check.reason)
    t.update(tRef, { returnDate, history: (trip.history || []).concat([{ action: 'return', from: trip.returnDate, to: returnDate, by: user || '', at: now() }]) })
    t.set(hold.ref, { holds: U.holdsAfterFix(hold.holds, tripId, U.loadingDay(trip.startDate), returnDate) })
  })
}

async function cancelTrip (tripId, { user }) {
  let orders = []
  await db.runTransaction(async t => {
    const { ref: tRef, trip } = await readTrip(t, tripId)
    const editable = U.checkTripEditable(trip)
    if (!editable.ok) throw new R.BookingError(editable.reason)
    const hold = await readHold(t, trip.regNum)
    const read = []
    for (const id of trip.orderIds || []) read.push(await readOrder(t, id))
    t.update(tRef, { status: 'cancelled', history: (trip.history || []).concat([{ action: 'cancel', by: user || '', at: now() }]) })
    t.set(hold.ref, { holds: U.holdsAfterRelease(hold.holds, tripId) })
    read.forEach(({ ref, b }) => {
      // A cancelled order on the trip is still written (the read must be).
      if (!R.isActive(b.status)) { t.update(ref, { upTripId: null }); return }
      t.update(ref, pooledPatch(b, null, { action: 'upTripCancelled', tripId, by: user || '' }))
    })
    orders = read.map(r => r.b).filter(b => R.isActive(b.status))
  })
  return { orders }
}

async function setStatus (tripId, from, to, action, user, extra) {
  await db.runTransaction(async t => {
    const { ref, trip } = await readTrip(t, tripId)
    if (trip.status !== from) throw new R.BookingError(from === 'fixed' ? 'UP_TRIP_SENT' : 'UP_TRIP_CLOSED')
    t.update(ref, Object.assign({ status: to }, extra || {}, {
      history: (trip.history || []).concat([Object.assign({ action, by: user || '', at: now() }, extra || {})])
    }))
  })
}
const markSent = (id, { user }) => setStatus(id, 'fixed', 'sent', 'sent', user)
const unmarkSent = (id, { user }) => setStatus(id, 'sent', 'fixed', 'unsent', user)
// A truck back early is free early: the hold is shortened to the day it really
// returned (and dropped if that is before the hold began), so the plate can be
// fixed onto another trip without anyone editing a hold by hand.
// `today` is a test seam only: trips can be fixed only in the future, so the
// emulator test passes a day after one left. The page never passes it.
async function closeTrip (id, { actualReturn, user, today }) {
  const back = actualReturn || R.todayStr()
  await db.runTransaction(async t => {
    const { ref, trip } = await readTrip(t, id)
    const hold = await readHold(t, trip.regNum)
    if (trip.status !== 'sent') throw new R.BookingError('UP_TRIP_CLOSED')
    const check = U.checkActualReturn(trip, back, today || R.todayStr())
    if (!check.ok) throw new R.BookingError(check.reason)
    const from = U.loadingDay(trip.startDate)
    const to = back < trip.returnDate ? back : trip.returnDate
    const rest = U.holdsAfterRelease(hold.holds, id)
    t.update(ref, {
      status: 'done',
      actualReturn: back,
      history: (trip.history || []).concat([{ action: 'done', actualReturn: back, by: user || '', at: now() }])
    })
    t.set(hold.ref, { holds: to < from ? rest : rest.concat([{ tripId: id, from, to }]) })
  })
}

// Every trip still open (fixed or sent), whatever its date -- รถใหญ่ต่างจังหวัด's
// list. A date window let a sent trip nobody closed drop off the page while it
// still held its truck. One field, so no composite index.
function subscribeOpenTrips (cb, onError) {
  return TRIP_COL().where('status', 'in', ['fixed', 'sent']).onSnapshot(snap => {
    const rows = []
    snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
    rows.sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)))
    cb(rows)
  }, onError)
}

function subscribeTrips (from, cb, onError) {
  return TRIP_COL().where('startDate', '>=', from).onSnapshot(snap => {
    const rows = []
    snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
    rows.sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)))
    cb(rows)
  }, onError)
}

// เพิ่ม RT on an up-country order. No figures move (a return is ฿0 and 0 CBM),
// but once the trip is sent the list may already be keyed into managecar, so
// the trip is read -- and, by the Web SDK v1 rule, written: the history line.
async function attachRtUp (orderId, { fields, user, role, identities }) {
  const rtNo = String((fields && fields.rtNo) || '').trim()
  if (!rtNo) throw new R.BookingError('NOT_FOUND')
  await db.runTransaction(async t => {
    const { ref, b } = await readOrder(t, orderId)
    const mayAttach = U.canManageOrder(b, role, identities)
    const check = RJ.checkAttachRt(b, { mayAttach })
    if (!check.ok) throw new R.BookingError(check.reason)
    if (b.upTripId) {
      const { ref: tRef, trip } = await readTrip(t, b.upTripId)
      const editable = U.checkTripEditable(trip)
      if (!editable.ok) throw new R.BookingError(editable.reason)
      t.update(tRef, { history: (trip.history || []).concat([{ action: 'attachRt', orderId, rtNo, by: user || '', at: now() }]) })
    }
    t.update(ref, {
      rtNo, rtDocEntry: fields.rtDocEntry || null, jobType: 'exchange',
      prodListRt: fields.prodListRt || [], rtItemCount: fields.rtItemCount || 0,
      history: log(b, { action: 'attachRt', rtNo, by: user || '' })
    })
  })
}

function subscribeHolds (cb, onError) {
  return db.collection('truckHold').onSnapshot(snap => {
    const byPlate = {}
    snap.forEach(d => { byPlate[d.id] = d.data().holds || [] })
    cb(byPlate)
  }, onError)
}

// บันทึกแผน: the plan on the trip, each order dated with its own drop day, and
// -- with useReturn -- the suggested return date and the truck hold with it,
// all in one transaction. Every order is read (to know its date) and so every
// order is written: the Web SDK v1 rejects a read it does not write. An order
// whose date does not change gets no history line.
async function savePlan ({ tripId, plan, useReturn, user, today }) {
  const changed = []
  let returnDate = null
  await db.runTransaction(async t => {
    changed.length = 0 // the callback re-runs on contention
    returnDate = null
    const { ref: tRef, trip } = await readTrip(t, tripId)
    const check = P.checkPlanSave(trip, plan, today || R.todayStr())
    if (!check.ok) throw new R.BookingError(check.reason)
    // A sent trip never takes the suggestion down or up on request; only a longer
    // return is written, because the truck really is away that long.
    const sent = trip.status === 'sent'
    const newReturn = sent ? P.longerReturn(trip, plan) : (useReturn && plan.suggestedReturn !== trip.returnDate ? plan.suggestedReturn : null)
    const holdWanted = sent ? !!newReturn : !!useReturn
    const hold = holdWanted ? await readHold(t, trip.regNum) : null
    const read = []
    for (const id of trip.orderIds) read.push(await readOrder(t, id))
    if (holdWanted) {
      const rc = U.checkReturnDate(Object.assign({ id: tripId }, trip), sent ? newReturn : plan.suggestedReturn, hold.holds)
      if (!rc.ok) throw new R.BookingError(rc.reason)
    }
    const dates = P.orderDates(plan)
    const entry = { action: 'plan', days: plan.days.length, by: user || '', at: now() }
    const upd = { plan: Object.assign(P.storedPlan(plan), { savedBy: user || '', savedAt: now() }) }
    if (newReturn) {
      upd.returnDate = newReturn
      entry.returnFrom = trip.returnDate
      entry.returnTo = newReturn
      returnDate = newReturn
    }
    upd.history = (trip.history || []).concat([entry])
    t.update(tRef, upd)
    if (holdWanted) t.set(hold.ref, { holds: U.holdsAfterFix(hold.holds, tripId, U.loadingDay(trip.startDate), sent ? newReturn : plan.suggestedReturn) })
    read.forEach(({ ref, b }) => {
      const to = dates[b.id]
      if (!to || to === b.date) { t.update(ref, { date: b.date || null }); return }
      t.update(ref, { date: to, history: log(b, { action: 'upPlan', from: b.date, to, tripId, by: user || '' }) })
      changed.push(Object.assign({}, b, { date: to }))
    })
  })
  return { changed, returnDate }
}

// ยกเลิกแผน: the plan comes off the trip and every order goes back to the
// trip's start date, which is what an unplanned trip gives them. The return
// date and the truckHold stay -- staff may have set them on purpose, and
// แก้วันกลับ changes them. Returns the orders whose date moved, for SAP.
async function clearPlan ({ tripId, user }) {
  const changed = []
  await db.runTransaction(async t => {
    changed.length = 0 // the callback re-runs on contention
    const { ref: tRef, trip } = await readTrip(t, tripId)
    const check = P.checkPlanClear(trip)
    if (!check.ok) throw new R.BookingError(check.reason)
    const read = []
    for (const id of trip.orderIds) read.push(await readOrder(t, id))
    t.update(tRef, { plan: null, history: (trip.history || []).concat([{ action: 'planClear', by: user || '', at: now() }]) })
    read.forEach(({ ref, b }) => {
      const to = trip.startDate
      if (b.date === to) { t.update(ref, { date: to }); return }
      t.update(ref, { date: to, history: log(b, { action: 'upPlanClear', from: b.date, to, tripId, by: user || '' }) })
      changed.push(Object.assign({}, b, { date: to }))
    })
  })
  return { changed }
}

// --- ส่งเข้าจัดรถ ----------------------------------------------------------------
// One click replaces keying a planned trip into managecar: the view checks SAP
// (P.checkSend), takes a number (freeTransNo), locks the trip (lockSend), calls
// UpdateSo / UpdateRT per order, then writes the rows (writeSendRows) and
// finishes (finishSend). See docs/superpowers/specs/2026-10-13-multi-day-trips-design.md.

// The first number from last+1 that no deliveryList row holds. managecar takes
// GetTransNoLast + 1 without looking; this narrows the race, it cannot close it
// (writeSendRows checks again).
async function freeTransNo (last) {
  for (let n = Number(last) + 1, i = 0; i < 20; n++, i++) {
    const snap = await LIST_COL().where('transNo', '==', String(n)).limit(1).get()
    if (snap.empty) return String(n)
  }
  throw new R.BookingError('UP_SEND_CLASH')
}

// Locks the trip before any SAP call: from here no order joins or leaves
// (status 'sent' means that already) and a second press is refused.
async function lockSend (tripId, { transNo, driver, user }) {
  await db.runTransaction(async t => {
    const { ref, trip } = await readTrip(t, tripId)
    if (trip.status !== 'fixed') throw new R.BookingError('UP_SEND_STATUS')
    if (!P.planIsCurrent(Object.assign({}, trip))) throw new R.BookingError('UP_PLAN_STALE')
    const at = now()
    t.update(ref, {
      status: 'sent', transNo: String(transNo), driver, sendState: 'pending', sentAt: at, sentBy: user || '',
      sheetPlan: trip.plan,
      history: (trip.history || []).concat([{ action: 'send', transNo: String(transNo), driver, by: user || '', at }])
    })
  })
}

// The sheet every row of a sent trip is written onto: the departure day as
// deliveryDate (as dispatch keys a 2-3 day trip), the loading day, the return
// day as the last day it may still deliver.
function sendSheet (trip) {
  const driver = String(trip.driver || '')
  return {
    transNo: String(trip.transNo),
    loadDate: U.loadingDay(trip.startDate),
    deliveryDate: trip.startDate,
    regNum: trip.regNum,
    driver,
    driverName: driver.split(' - ').slice(1).join(' - '),
    tripEnd: trip.returnDate
  }
}

// Every deliveryList row on one number. Equality only: no composite index.
async function sendRowsOf (transNo) {
  const snap = await LIST_COL().where('transNo', '==', String(transNo)).get()
  const rows = []
  snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
  return rows
}

// A row and its timeline per job, in one batch, skipping any order this trip
// already wrote -- which is what makes ลองอีกครั้ง safe to press. A row of
// ANOTHER trip on this number means the number was taken meanwhile: stop.
async function writeSendRows (tripId, { sheet, jobs }) {
  const existing = await sendRowsOf(sheet.transNo)
  // Ours: written by this trip, or added to this sheet in managecar since (same
  // departure day and plate, no upTripId). Anything else holds the number.
  const foreign = r => r.upTripId ? r.upTripId !== tripId : (r.deliveryDate !== sheet.deliveryDate || r.regNum !== sheet.regNum)
  if (existing.some(foreign)) throw new R.BookingError('UP_SEND_CLASH')
  const done = {}
  existing.forEach(r => { if (r.sourceSlotId) done[r.sourceSlotId] = true })
  const todo = (jobs || []).filter(j => !done[j.sourceSlotId])
  if (todo.length) {
    const batch = db.batch()
    todo.forEach(j => {
      const p = DR.pair(Object.assign({}, j, { upTripId: tripId }), sheet)
      batch.set(LIST_COL().doc(), p.row)
      batch.set(TIMELINE_COL().doc(), p.timeline)
    })
    await batch.commit()
  }
  return { written: todo.length, skipped: (jobs || []).length - todo.length }
}

async function finishSend (tripId, { sheetPlan } = {}) {
  await TRIP_COL().doc(tripId).update(sheetPlan ? { sendState: 'done', sheetPlan } : { sendState: 'done' })
}

// A row belongs to a sent trip's sheet when this trip wrote it, or when dispatch
// added it in managecar afterwards (same departure day and plate, no upTripId).
function onSheet (r, trip, tripId) {
  return r.upTripId ? r.upTripId === tripId : (r.deliveryDate === trip.startDate && r.regNum === trip.regNum)
}

// What a row is tracked by: the RT for a pickup, else the SO.
const rowRef = r => String(r.status === 'RT' ? r.rtNo : r.soNo)

// The ids of the timelines this sheet wrote for these numbers: matched by
// trackNo AND the sheet's date and plate, so an older delivery of the same SO
// keeps its record. `in` takes ten at a time; no composite index.
// Known limit: if dispatch keyed the same SO onto this sheet again in managecar
// (a second row, no upTripId), that row's timeline matches too -- same trackNo,
// date and plate -- and goes with this trip's.
async function sheetTimelines (trip, nos) {
  const list = Array.from(new Set((nos || []).map(String).filter(Boolean)))
  const ids = []
  for (let i = 0; i < list.length; i += 10) {
    const snap = await TIMELINE_COL().where('trackNo', 'in', list.slice(i, i + 10)).get()
    snap.forEach(d => { const x = d.data(); if (x.date === trip.startDate && x.regNum === trip.regNum) ids.push(d.id) })
  }
  return ids
}

// ยกเลิกการส่ง, after the view has warned about stamped work (stampGuardForListDoc
// on each row) and called CancelSo / CancelRt. Deletes this trip's rows and
// their timelines in pairs -- a timeline is matched by trackNo AND the sheet's
// date and plate, so an older delivery of the same SO keeps its record -- then
// puts the trip back to `fixed`. Rows another trip wrote on the number are left;
// rows added in managecar to this sheet go with it.
async function unsend (tripId, { user }) {
  const tripSnap = await TRIP_COL().doc(tripId).get()
  const trip = tripSnap.exists ? tripSnap.data() : null
  if (!trip || trip.status !== 'sent' || !trip.transNo) throw new R.BookingError('UP_SEND_NOT_SENT')
  const rows = (await sendRowsOf(trip.transNo)).filter(r => onSheet(r, trip, tripId))
  const timelines = await sheetTimelines(trip, rows.map(rowRef))
  const batch = db.batch()
  rows.forEach(r => batch.delete(LIST_COL().doc(r.id)))
  timelines.forEach(id => batch.delete(TIMELINE_COL().doc(id)))
  await batch.commit()
  await db.runTransaction(async t => {
    const { ref, trip: cur } = await readTrip(t, tripId)
    t.update(ref, {
      status: 'fixed', transNo: null, driver: null, sendState: null, sentAt: null, sentBy: null,
      history: (cur.history || []).concat([{ action: 'unsend', transNo: trip.transNo, rows: rows.length, by: user || '', at: now() }])
    })
  })
  return { deleted: rows.length }
}

// An order leaves a trip already sent to จัดรถ: pooled, postponed to a week, or
// cancelled. Its deliveryList rows and timelines on THIS sheet go with it in the
// same transaction (found beforehand -- a transaction cannot run a query -- and
// deleted blind: a delete needs no read). The view has already asked SAP
// (CancelSo / CancelRt on a read-back that holds this number) and shown the
// stamp guard. A cancel closes the shop's pending cancel request as confirmed.
// After the loading day (UP_SENT_LOCKED) only an order with NO row on the sheet
// may still leave -- one added inside the window and never sent: nothing
// dispatch can see changes. Every other check stays.
// The timelines found are matched by sheetTimelines, so a managecar row of the
// same SO on this sheet would have its timeline deleted too (see there).
// `afterLookup` is a test seam: awaited between the lookup and the transaction.
// `today` is a test seam too; a view leaves it out (R.todayStr() at call time).
async function takeOffSentTrip (orderId, { mode, week, reason, user, role, today, afterLookup } = {}) {
  if (!R.isStaffRole(role)) throw new R.BookingError('UP_NOT_ALLOWED')
  if (['pool', 'postpone', 'cancel'].indexOf(mode) === -1) throw new R.BookingError('UP_BAD_MODE')
  if (mode === 'postpone' && !week) throw new R.BookingError('UP_BAD_WEEK')
  const cfg = await getUpConfig()
  const day = today || R.todayStr()
  // The sheet rows, found before the transaction from the trip the order is on now.
  let found = { tripId: null, rows: [], timelines: [] }
  const pre = await SLOT_COL().doc(orderId).get()
  const tripId0 = pre.exists ? pre.data().upTripId : null
  if (tripId0) {
    const tSnap = await TRIP_COL().doc(tripId0).get()
    if (tSnap.exists && tSnap.data().transNo) {
      const trip0 = tSnap.data()
      const rows = (await sendRowsOf(trip0.transNo)).filter(r => onSheet(r, trip0, tripId0) && r.sourceSlotId === orderId)
      found = {
        tripId: tripId0, transNo: trip0.transNo, startDate: trip0.startDate, regNum: trip0.regNum,
        rows, timelines: await sheetTimelines(trip0, rows.map(rowRef))
      }
    }
  }
  if (afterLookup) await afterLookup()
  await db.runTransaction(async t => {
    const { ref, b } = await readOrder(t, orderId)
    if (!R.isActive(b.status)) throw new R.BookingError('ALREADY_CANCELLED')
    if (!b.upTripId) throw new R.BookingError('UP_NOT_ON_TRIP')
    const { ref: tRef, trip } = await readTrip(t, b.upTripId)
    const editable = U.checkSentEdit(trip, day)
    // A rowless order is past only the window, never the rest (sent, a number).
    const rowless = !found.rows.length && editable.reason === 'UP_SENT_LOCKED'
    if (!editable.ok && !rowless) throw new R.BookingError(editable.reason)
    if ((trip.orderIds || []).length === 1) throw new R.BookingError('UP_LAST_ORDER')
    // The rows were found for one sheet: if the order's trip, or that trip's number,
    // departure day or plate, is not what the lookup saw, nothing is deleted.
    if (found.tripId !== b.upTripId || found.transNo !== trip.transNo ||
      found.startDate !== trip.startDate || found.regNum !== trip.regNum) throw new R.BookingError('UP_SHEET_CHANGED')
    const closesRequest = mode === 'cancel' && X.isPending(b) && b.proposal.kind === 'cancel'
    if (X.isPending(b) && !closesRequest) throw new R.BookingError('PROPOSAL_PENDING')
    if (mode === 'postpone') {
      const check = U.checkUpOrder(Object.assign({}, b, { earliestWeek: week }), day)
      if (!check.ok) throw new R.BookingError(check.reason)
    }
    const tripPatch = Object.assign(U.tripAfterRemove(trip, orderId, b, cfg), {
      plan: P.planWithout(trip.plan, orderId),
      history: (trip.history || []).concat([{ action: 'sentRemove', mode, orderId, by: user || '', at: now() }])
    })
    if (trip.sheetPlan) tripPatch.sheetPlan = P.planWithout(trip.sheetPlan, orderId)
    t.update(tRef, tripPatch)
    if (mode === 'cancel') {
      const patch = cancelPatch(b, { user, reason })
      if (closesRequest) Object.assign(patch, X.decisionPatch(b, { status: 'confirmed', decidedBy: user || '', decidedAt: now() }, false))
      t.update(ref, patch)
    } else {
      const postpone = mode === 'postpone'
      t.update(ref, pooledPatch(b, postpone ? week : null, {
        action: postpone ? 'upPostpone' : 'upRemove', from: b.date, tripId: b.upTripId, week: postpone ? week : null, by: user || ''
      }))
    }
    found.rows.forEach(r => t.delete(LIST_COL().doc(r.id)))
    found.timelines.forEach(id => t.delete(TIMELINE_COL().doc(id)))
  })
  return { rows: found.rows.length }
}

// A re-plan's changes (P.sheetChanges) written onto the sheet's rows in one
// batch. A changed window also goes onto the row's timeline, element 0 only; a
// timeline not found is skipped and the row still updates.
async function updateSheetRows (tripId, changes) {
  const list = changes || []
  if (!list.length) return { rows: 0 }
  const tripSnap = await TRIP_COL().doc(tripId).get()
  if (!tripSnap.exists) throw new R.BookingError('UP_TRIP_CLOSED')
  const trip = tripSnap.data()
  const batch = db.batch()
  let rows = 0
  for (const c of list) {
    const rowSnap = await LIST_COL().doc(c.rowId).get()
    if (!rowSnap.exists) continue // a row that is gone would fail the whole batch
    batch.update(LIST_COL().doc(c.rowId), c.patch)
    rows++
    if (!c.timeChanged || !c.patch.time) continue // never blank a window
    for (const id of await sheetTimelines(trip, [rowRef(rowSnap.data())])) {
      const tSnap = await TIMELINE_COL().doc(id).get()
      const line = tSnap.exists ? (tSnap.data().timeLine || []).slice() : []
      if (!line.length) continue
      line[0] = Object.assign({}, line[0], { time: c.patch.time })
      batch.update(TIMELINE_COL().doc(id), { timeLine: line })
    }
  }
  if (rows) await batch.commit()
  return { rows }
}

// After a save lengthened the return: every row on this trip's sheet carries the
// new last day. A row another trip wrote on the number is left alone.
async function setSheetTripEnd (tripId) {
  const snap = await TRIP_COL().doc(tripId).get()
  if (!snap.exists) throw new R.BookingError('UP_TRIP_CLOSED')
  const trip = snap.data()
  if (!trip.transNo) return { rows: 0 }
  const rows = (await sendRowsOf(trip.transNo)).filter(r => onSheet(r, trip, tripId) && r.tripEnd !== trip.returnDate)
  if (!rows.length) return { rows: 0 }
  const batch = db.batch()
  rows.forEach(r => batch.update(LIST_COL().doc(r.id), { tripEnd: trip.returnDate }))
  await batch.commit()
  return { rows: rows.length }
}

// The planner's settings (upcountryPlanConfig/default). Staff edit them; not
// upcountryConfig, whose minimum is the managing committee's decision.
async function getPlanConfig () {
  const snap = await PLAN_CFG_REF().get()
  return P.normalizePlanConfig(snap.exists ? snap.data() : null)
}

async function setPlanConfig (raw, { user }) {
  const clean = P.normalizePlanConfig(raw)
  await db.runTransaction(async t => {
    const snap = await t.get(PLAN_CFG_REF())
    const prev = snap.exists ? snap.data() : {}
    t.set(PLAN_CFG_REF(), Object.assign({}, clean, {
      updatedBy: user || '', updatedAt: now(),
      history: (prev.history || []).concat([{ to: clean, by: user || '', at: now() }])
    }))
  })
  return clean
}

// วันปิดร้าน: shopClosedDay/<SAP cardCode>.
function subscribeShopClosedDays (cb, onError) {
  return SHOP_COL().onSnapshot(snap => {
    const rows = []
    snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
    rows.sort((a, b) => String(a.cardName).localeCompare(String(b.cardName), 'th'))
    cb(rows)
  }, onError)
}

async function setShopClosedDay ({ cardCode, cardName, closedDays, note, user }) {
  const check = P.checkShopClosedDay({ cardCode, cardName, closedDays })
  if (!check.ok) throw new R.BookingError(check.reason)
  const ref = SHOP_COL().doc(check.cardCode)
  await db.runTransaction(async t => {
    const snap = await t.get(ref)
    const prev = snap.exists ? snap.data() : {}
    t.set(ref, {
      cardCode: check.cardCode, cardName: check.cardName, closedDays: check.closedDays,
      note: String(note || '').trim().slice(0, 200),
      updatedBy: user || '', updatedAt: now(),
      history: (prev.history || []).concat([{ closedDays: check.closedDays, cardName: check.cardName, by: user || '', at: now() }])
    })
  })
}

function deleteShopClosedDay (cardCode) {
  return SHOP_COL().doc(String(cardCode || '').trim()).delete()
}

module.exports = {
  INSTALL_MODES, getUpConfig, createUpOrder, setInstallMode,
  upCancelWrites, cancelPatch, cancelUpOrder, subscribeOrders,
  fixTrip, addToTrip, postponeOrder, removeFromTrip, refreshFromSap, retireDispatched, setReturnDate, cancelTrip, markSent, unmarkSent, closeTrip,
  freeTransNo, lockSend, sendSheet, onSheet, sendRowsOf, writeSendRows, finishSend, unsend,
  sheetTimelines, takeOffSentTrip, updateSheetRows, setSheetTripEnd,
  savePlan, clearPlan, getPlanConfig, setPlanConfig, subscribeShopClosedDays, setShopClosedDay, deleteShopClosedDay,
  subscribeTrips, subscribeOpenTrips, subscribeHolds, attachRtUp,
  SLOT_COL, TRIP_COL, HOLD_REF, readOrder, log, now
}
